import React, { useState, useEffect } from "react";
import {
    CASE_TYPE_OPTIONS,
    CASE_TYPE_FIELDS,
    STANDARD_HEADER_FIELDS,
} from "../data/caseTypeConfig";

// ── BADGE helpers ──
const AiBadge = () => (
    <span className="source-badge badge-ai">
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none">
            <rect width="24" height="24" rx="6" fill="#7c3aed" />
            <circle cx="12" cy="12" r="5" stroke="#fff" strokeWidth="2.5" />
        </svg>
        AI
    </span>
);

const SfBadge = () => (
    <span className="source-badge badge-sf">
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none">
            <rect width="24" height="24" rx="6" fill="#00a1e0" />
            <path d="M6 12a6 6 0 1 1 12 0" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        Salesforce
    </span>
);

const EmailBadge = () => <span className="source-badge badge-email">From Email</span>;
const MissingBadge = () => <span className="source-badge badge-missing">Missing</span>;
const YouBadge = () => <span className="source-badge badge-you">You</span>;



const GetFieldBadge = ({ value, source }) => {
    if (!value && value !== true && value !== false) return <MissingBadge />;
    if (source === "user") return <YouBadge />;
    if (source === "ai") return <AiBadge />;
    if (source === "sf") return <SfBadge />;
    if (source === "email") return <EmailBadge />;
    return null;
};

const CaseForm = ({ 
    fields, 
    handleFieldChange, 
    filledCount, 
    missingCount, 
    dynamicMissing, 
    onSubmit, 
    creatingCase, 
    sfContext, 
    policies, 
    fieldSources, 
    attachments, 
    onToggleAttachment,
    loading,
    onAnalyzeAttachmentsAlso,
    analyseAttachments 
}) => {
    // handleSubmit now always proceeds even with missing fields
    console.log("checking attachments", attachments)
    const handleSubmit = () => {
        onSubmit();
    };
    console.log("checking value of fields", fields)
    return (
        <div className="case-form-wrapper">

            {/* Intent Box */}
            <div className="intent-box">
                <span className="intent-badge">{fields.caseType}</span>
                <p className="intent-desc">
                    {fields.description}
                </p>
            </div>

            {/* Case Fields Header */}
            <div className="case-fields-header-wrapper">
                <div className="case-fields-header">
                    <span className="case-fields-title">Case Fields</span>
                    <div className="case-fields-counts">
                        <span className="count-filled">{filledCount} filled</span>
                        <span className="count-missing">{missingCount} missing</span>
                    </div>
                </div>
                {attachments && attachments.length > 0 && !analyseAttachments && (
                    <div className="analyze-attachments-also-row">
                        <button 
                            type="button"
                            className="analyze-attachments-also-btn"
                            onClick={onAnalyzeAttachmentsAlso}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner spinner-white" style={{ width: 10, height: 10, borderWidth: 1.5, marginRight: 6 }} />
                                    Analyzing...
                                </>
                            ) : (
                                "Analyze Attachments also"
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* ── Scrollable Fields Area ── */}
            <div className="fields-scroll-area">
                <div className="form-field-group">
                    <label className="form-label">
                        Subject <span className="required-star">*</span>
                        <GetFieldBadge value={fields.subject} source={fieldSources.subject} />
                    </label>
                    <input className="form-input" type="text" value={fields.subject}
                        onChange={(e) => handleFieldChange("subject", e.target.value)} />
                </div>

                {/* ── Contact ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Contact <span className="required-star">*</span>
                        <GetFieldBadge value={fields.contactId} source={fieldSources.contactId} />
                    </label>
                    {sfContext.contacts && sfContext.contacts.length > 0 ? (
                        <select
                            className={`form-select${!fields.contactId ? " input-missing" : ""}`}
                            value={fields.contactId}
                            onChange={(e) => handleFieldChange("contactId", e.target.value)}
                        >
                            <option value="">Select contact...</option>
                            {sfContext.contacts.map((c) => (
                                <option key={c.contactId} value={c.contactId}>
                                    {c.name}
                                </option>
                            ))}
                            {/* Fallback if value is a name from email/AI instead of ID */}
                            {fields.contactId && !sfContext.contacts.some(c => c.contactId === fields.contactId) && (
                                <option value={fields.contactId}>{fields.contactId} (Extracted)</option>
                            )}
                        </select>
                    ) : (
                        <input
                            className={`form-input${!fields.contactId ? " input-missing" : ""}`}
                            type="text"
                            placeholder="Enter contact name..."
                            value={fields.contactId}
                            onChange={(e) => handleFieldChange("contactId", e.target.value)}
                        />
                    )}
                </div>

                {/* ── Related Policy ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Related Policy <span className="required-star">*</span>
                        <GetFieldBadge value={fields.policyId} source={fieldSources.policyId} />
                    </label>
                    {policies && policies.length > 0 ? (
                        <select
                            className={`form-select${!fields.policyId ? " input-missing" : ""}`}
                            value={fields.policyId}
                            onChange={(e) => handleFieldChange("policyId", e.target.value)}
                        >
                            <option value="">Select policy...</option>
                            {policies.map((p) => (
                                <option key={p.policyId || p.id} value={p.policyId || p.id}>
                                    {p.policyNumber} ({p.type || p.lineOfBusiness || "N/A"})
                                </option>
                            ))}
                            {/* Fallback for AI extracted policy number (not ID) */}
                            {fields.policyId && !policies.some(p => (p.policyId || p.id) === fields.policyId) && (
                                <option value={fields.policyId}>{fields.policyId} (Extracted)</option>
                            )}
                        </select>
                    ) : (
                        <input
                            className={`form-input${!fields.policyId ? " input-missing" : ""}`}
                            type="text"
                            placeholder="Enter policy number..."
                            value={fields.policyId}
                            onChange={(e) => handleFieldChange("policyId", e.target.value)}
                        />
                    )}
                </div>

                {/* ── Case Type ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Case Type <span className="required-star">*</span>
                        <GetFieldBadge value={fields.caseType} source={fieldSources.caseType} />
                    </label>
                    <select
                        className={`form-select${!fields.caseType ? " input-missing" : ""}`}
                        value={fields.caseType}
                        onChange={(e) => handleFieldChange("caseType", e.target.value)}>
                        <option value="">Select case type...</option>
                        {CASE_TYPE_OPTIONS.map((caseType) => (
                            <option key={caseType} value={caseType}>{caseType}</option>
                        ))}
                        {fields.caseType && !CASE_TYPE_OPTIONS.includes(fields.caseType) && (
                            <option value={fields.caseType}>{fields.caseType} (Extracted)</option>
                        )}
                    </select>
                </div>

                {/* ── Priority ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Priority <span className="required-star">*</span>
                        <GetFieldBadge value={fields.priority} source={fieldSources.priority} />
                    </label>
                    <select
                        className={`form-select${!fields.priority ? " input-missing" : ""}`}
                        value={fields.priority}
                        onChange={(e) => handleFieldChange("priority", e.target.value)}>
                        <option value="">Select priority...</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Urgent">Urgent</option>
                    </select>
                </div>

                {/* ── Description ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Description <span className="required-star">*</span>
                        <GetFieldBadge value={fields.description} source={fieldSources.description} />
                    </label>
                    <textarea
                        className={`form-textarea${!fields.description ? " input-missing" : ""}`}
                        value={fields.description}
                        onChange={(e) => handleFieldChange("description", e.target.value)}
                        rows={4}
                    />
                </div>

                {/* ── Standard Header Fields ── */}
                {STANDARD_HEADER_FIELDS.map((fieldDef) => (
                    <div className="form-field-group" key={fieldDef.key}>
                        <label className="form-label">
                            {fieldDef.label}
                            <GetFieldBadge value={fields[fieldDef.key]} source={fieldSources[fieldDef.key]} />
                        </label>
                        <input
                            className={`form-input${!fields[fieldDef.key] ? " input-missing" : ""}`}
                            type={fieldDef.type === "date" ? "date" : "text"}
                            value={fields[fieldDef.key] || ""}
                            onChange={(e) => handleFieldChange(fieldDef.key, e.target.value)}
                        />
                    </div>
                ))}

                {/* ── Type-Specific Fields (dynamic sections) ── */}
                {fields.caseType && CASE_TYPE_FIELDS[fields.caseType]?.sections.map((section) => (
                    <div className="form-section" key={section.title}>
                        <h4 className="form-section-title">{section.title}</h4>
                        {section.fields.map((fieldDef) => {
                            const val = fields[fieldDef.key] || "";
                            const source = fieldSources[fieldDef.key];

                            return (
                                <div className="form-field-group" key={fieldDef.key}>
                                    <label className="form-label">
                                        {fieldDef.label}
                                        <GetFieldBadge value={val} source={source} />
                                    </label>

                                    {fieldDef.sfMultiSelect ? (
                                        <div className="multiselect-checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                            {fieldDef.options.map((opt) => {
                                                const selectedValues = typeof val === "string"
                                                    ? val.split(/;\s*|,\s*/).map(v => v.trim()).filter(Boolean)
                                                    : Array.isArray(val) ? val : [];
                                                const isChecked = selectedValues.includes(opt);
                                                return (
                                                    <label key={opt} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px', color: '#333' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={(e) => {
                                                                let newValues;
                                                                if (e.target.checked) {
                                                                    newValues = [...selectedValues, opt];
                                                                } else {
                                                                    newValues = selectedValues.filter(v => v !== opt);
                                                                }
                                                                handleFieldChange(fieldDef.key, newValues.join("; "));
                                                            }}
                                                            style={{ marginRight: 8, cursor: 'pointer' }}
                                                        />
                                                        {opt}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    ) : fieldDef.type === "select" ? (
                                        <select
                                            className={`form-select${!val ? " input-missing" : ""}`}
                                            value={val}
                                            onChange={(e) => handleFieldChange(fieldDef.key, e.target.value)}
                                        >
                                            <option value="">Select {fieldDef.label.toLowerCase()}...</option>
                                            {fieldDef.options.map((opt) => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                            {val && !fieldDef.options.includes(val) && (
                                                <option value={val}>{val} (Extracted)</option>
                                            )}
                                        </select>
                                    ) : fieldDef.type === "date" ? (
                                        <input
                                            className={`form-input${!val ? " input-missing" : ""}`}
                                            type="date"
                                            value={val}
                                            onChange={(e) => handleFieldChange(fieldDef.key, e.target.value)}
                                        />
                                    ) : fieldDef.type === "textarea" ? (
                                        <textarea
                                            className={`form-textarea${!val ? " input-missing" : ""}`}
                                            value={val}
                                            onChange={(e) => handleFieldChange(fieldDef.key, e.target.value)}
                                            rows={3}
                                        />
                                    ) : (
                                        <input
                                            className={`form-input${!val ? " input-missing" : ""}`}
                                            type="text"
                                            value={val}
                                            onChange={(e) => handleFieldChange(fieldDef.key, e.target.value)}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}

                {/* ── Attachments Section ── */}

                {attachments && attachments.length > 0 && (
                    <div className="attachments-section">
                        <div className="attachments-header">
                            <span className="attachments-title">Email Attachments</span>
                            <span className="attachments-count">{attachments.filter(a => a.selected).length} selected</span>
                        </div>
                        <div className="attachments-list">
                            {attachments.map((att) => (
                                <div
                                    key={att.id}
                                    className={`attachment-item ${att.selected ? "selected" : ""}`}
                                    onClick={() => onToggleAttachment(att.id)}
                                >
                                    <input
                                        type="checkbox"
                                        className="attachment-checkbox"
                                        checked={att.selected}
                                        readOnly
                                    />
                                    <div className="attachment-info">
                                        <span className="attachment-name" title={att.name}>{att.name}</span>
                                        <span className="attachment-meta">
                                            {(att.size / 1024).toFixed(1)} KB • {att.contentType}
                                        </span>
                                    </div>
                                    <div className="attachment-icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 2V12M12 12L8 8M12 12L16 8M20 12V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H12" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            </div>{/* end fields-scroll-area */}

            {/* ── Missing Fields Warning — FIXED BOTTOM ── */}
            {missingCount > 0 && (
                <div className="missing-warning-box">
                    <div className="missing-warning-title">
                        <span>⚠️</span>
                        <strong>{missingCount} Required Fields Missing</strong>
                    </div>
                    <p className="missing-warning-desc">
                        The AI will draft a follow-up email to ask the client for:{" "}
                        <span className="missing-warning-fields">{dynamicMissing.join(", ")}</span>
                    </p>
                </div>
            )}

            {/* ── Submit Button ── */}
            <button
                className={`submit-case-btn ${creatingCase ? "btn-disabled" : ""}`}
                onClick={handleSubmit}
                disabled={creatingCase}
            >
                {creatingCase ? (
                    <>
                        <div className="submit-btn-icon-wrapper">
                            <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                        </div>
                        <span className="btn-text">Creating Case...</span>
                    </>
                ) : (
                    <>
                        <div className="submit-btn-icon-wrapper">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="3" width="18" height="18" rx="2" stroke="white" strokeWidth="2.5" />
                                <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <span className="btn-text">Create Case &amp; Generate Follow-up Email!</span>
                    </>
                )}
            </button>


        </div>
    );
};

export default CaseForm;
